import { Question, DailyChallenge, DAILY_QUESTION_COUNT } from '../types/game';

// Mulberry32 seeded PRNG
function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function dateToSeed(dateStr: string): number {
  // YYYY-MM-DD → integer YYYYMMDD
  return parseInt(dateStr.replace(/-/g, ''), 10);
}

function getTodayUTC(): string {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getDailyChallenge(
  allQuestions: Question[],
  date?: string,
): DailyChallenge {
  const today = date || getTodayUTC();
  const seed = dateToSeed(today);
  const rng = mulberry32(seed);

  // Pool: only FREE tier questions so daily is accessible to everyone
  const pool = allQuestions.filter((q) => q.tier === 'FREE');

  // Shuffle pool using seeded RNG
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  // Pick DAILY_QUESTION_COUNT questions mixing difficulties
  // Try to get: 4 EASY, 3 MEDIUM, 3 HARD
  const easy = shuffled.filter((q) => q.difficulty === 'EASY');
  const medium = shuffled.filter((q) => q.difficulty === 'MEDIUM');
  const hard = shuffled.filter((q) => q.difficulty === 'HARD');

  const picked: Question[] = [
    ...easy.slice(0, 4),
    ...medium.slice(0, 3),
    ...hard.slice(0, 3),
  ];

  // If not enough of a difficulty, backfill from others
  while (picked.length < DAILY_QUESTION_COUNT && shuffled.length > picked.length) {
    const next = shuffled.find((q) => !picked.includes(q));
    if (next) picked.push(next);
    else break;
  }

  // Sort: EASY → MEDIUM → HARD
  const diffOrder = { EASY: 0, MEDIUM: 1, HARD: 2 };
  picked.sort((a, b) => diffOrder[a.difficulty] - diffOrder[b.difficulty]);

  return {
    date: today,
    seed,
    questionIds: picked.map((q) => q.id),
  };
}

export { getTodayUTC };
